/**
 * A assinatura que reconcilia (E147.F3, ADR-0101 §2.6): quando a resposta da assinatura não chega
 * (rede, tempo esgotado), o servidor pode ter assinado. A tela relê a requisição antes de oferecer
 * "Assinar" de novo:
 *
 * - ainda entre as certificações pendentes: nada foi assinado, e a tela diz para assinar de novo;
 * - fora delas: o pedido repetido do mesmo certificador recebe a assinatura gravada
 *   (`reconciliada`), sem assinar outra vez;
 * - sem resposta também na releitura: a tela diz que não conseguiu confirmar — repetir é seguro.
 *
 * Fica fora de `lib/certificacao.ts` porque chama as funções dele pelo módulo, como a página.
 */
import type { AxiosInstance } from 'axios';

import {
  assinarLaudo,
  CertificacaoError,
  getPendentes,
  type AssinaturaLaudo,
  type ChecklistRevisao,
} from '@/lib/certificacao';

export type DesfechoDaAssinatura =
  | { desfecho: 'assinada'; assinatura: AssinaturaLaudo }
  | { desfecho: 'nao_assinada' }
  | { desfecho: 'sem_confirmacao' };

function semResposta(err: unknown): boolean {
  return err instanceof CertificacaoError && err.reason === 'rede';
}

async function relerARequisicao(
  client: AxiosInstance,
  requisicaoId: string,
  checklist: ChecklistRevisao,
): Promise<DesfechoDaAssinatura> {
  try {
    const { itens } = await getPendentes(client);
    if (itens.some((item) => item.requisicao_id === requisicaoId)) {
      return { desfecho: 'nao_assinada' };
    }
    return {
      desfecho: 'assinada',
      assinatura: await assinarLaudo(client, requisicaoId, checklist),
    };
  } catch (err) {
    if (semResposta(err)) return { desfecho: 'sem_confirmacao' };
    throw err;
  }
}

export async function assinarComReconciliacao(
  client: AxiosInstance,
  requisicaoId: string,
  checklist: ChecklistRevisao,
): Promise<DesfechoDaAssinatura> {
  try {
    return {
      desfecho: 'assinada',
      assinatura: await assinarLaudo(client, requisicaoId, checklist),
    };
  } catch (err) {
    if (!semResposta(err)) throw err;
  }
  return relerARequisicao(client, requisicaoId, checklist);
}
